if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.viewer) return;

        /*
         * When the main gallery slide changes,
         * update the selected thumbnail.
         */
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        /*
         * Thumbnail click handling.
         */
        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (!button) return;

              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            });
        }

        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /*
       * Fired when the main gallery slider itself changes.
       */
      onSlideChanged(event) {
        if (!event.detail || !event.detail.currentElement) return;

        const mediaId =
          event.detail.currentElement.dataset.mediaId;

        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      /*
       * Main function responsible for changing
       * the large product image.
       */
      setActiveMedia(mediaId, prepend = false) {
        if (!mediaId || !this.elements.viewer) return;

        /*
         * Find the exact main media slide.
         */
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) {
          return;
        }

        /*
         * Remove active state from every main slide.
         */
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        /*
         * Mark selected main slide as active.
         */
        activeMedia.classList.add('is-active');

        /*
         * Do NOT rely on prepend to change the visible image.
         * We explicitly move the main gallery slider.
         */
        if (prepend) {
          const parent = activeMedia.parentElement;

          if (
            parent &&
            parent.firstElementChild !== activeMedia
          ) {
            parent.prepend(activeMedia);

            if (this.elements.thumbnails) {
              const activeThumbnail =
                this.elements.thumbnails.querySelector(
                  `[data-target="${mediaId}"]`
                );

              if (
                activeThumbnail &&
                activeThumbnail.parentElement &&
                activeThumbnail.parentElement.firstElementChild !==
                  activeThumbnail
              ) {
                activeThumbnail.parentElement.prepend(
                  activeThumbnail
                );
              }
            }

            /*
             * Reset Shopify slider calculations after
             * changing the DOM order.
             */
            if (
              this.elements.viewer.slider &&
              typeof this.elements.viewer.resetPages === 'function'
            ) {
              this.elements.viewer.resetPages();
            }
          }
        }

        this.preventStickyHeader();

        /*
         * IMPORTANT:
         * Move the actual large gallery to the selected slide.
         */
        window.requestAnimationFrame(() => {
          this.scrollMainMediaIntoView(activeMedia);
        });

        /*
         * Play video/media if required.
         */
        this.playActiveMedia(activeMedia);

        /*
         * Update thumbnail selection.
         */
        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      /*
       * Explicitly scroll the MAIN gallery.
       *
       * This is the important fix for your issue.
       */
      scrollMainMediaIntoView(activeMedia) {
        if (!activeMedia) return;

        const viewer = this.elements.viewer;

        /*
         * Shopify Dawn's slider-component exposes
         * the actual slider through .slider.
         */
        if (
          viewer.slider &&
          typeof viewer.slider.scrollTo === 'function'
        ) {
          const slider = viewer.slider;

          /*
           * Calculate the exact position of the selected
           * slide relative to the slider.
           */
          const sliderRect =
            slider.getBoundingClientRect();

          const mediaRect =
            activeMedia.getBoundingClientRect();

          const currentScrollLeft =
            slider.scrollLeft || 0;

          const targetLeft =
            currentScrollLeft +
            (mediaRect.left - sliderRect.left);

          slider.scrollTo({
            left: targetLeft,
            behavior: 'smooth',
          });

          return;
        }

        /*
         * Fallback for normal horizontal scrolling.
         */
        const parent = activeMedia.parentElement;

        if (parent && typeof parent.scrollTo === 'function') {
          const parentRect =
            parent.getBoundingClientRect();

          const mediaRect =
            activeMedia.getBoundingClientRect();

          const targetLeft =
            parent.scrollLeft +
            (mediaRect.left - parentRect.left);

          parent.scrollTo({
            left: targetLeft,
            behavior: 'smooth',
          });

          return;
        }

        /*
         * Final fallback.
         */
        activeMedia.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      /*
       * Update selected thumbnail.
       */
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        const button =
          thumbnail.querySelector('button');

        if (button) {
          button.setAttribute('aria-current', 'true');
        }

        /*
         * Make sure the selected thumbnail is visible.
         */
        if (
          typeof this.elements.thumbnails.isSlideVisible ===
          'function'
        ) {
          if (
            this.elements.thumbnails.isSlideVisible(
              thumbnail,
              10
            )
          ) {
            return;
          }
        }

        if (
          this.elements.thumbnails.slider &&
          typeof this.elements.thumbnails.slider.scrollTo ===
            'function'
        ) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      /*
       * Accessibility announcement.
       */
      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          if (
            window.accessibilityStrings &&
            window.accessibilityStrings.imageAvailable
          ) {
            this.elements.liveRegion.innerHTML =
              window.accessibilityStrings.imageAvailable.replace(
                '[index]',
                position
              );
          }

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        /*
         * Re-trigger load event.
         */
        image.src = image.src;
      }

      /*
       * Play the currently active media.
       */
      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      /*
       * Prevent sticky header from interfering with
       * gallery movement.
       */
      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      /*
       * Remove list semantics for desktop thumbnail layout.
       */
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        if (!this.elements.viewer.sliderItems) return;

        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute(
            'role',
            'presentation'
          );
        });
      }
    }
  );
}